import React from 'react';
import { auth, db } from '../../../services/firebase';
import { addDoc, collection, serverTimestamp, setDoc, doc } from 'firebase/firestore';
import useStockStore from '../../../stores/useStockStore';
import { useActiveBroker } from '../../../hooks/useActiveBroker';
import EditStockModal from './EditStockModal';
import { Trash2, Edit3 } from 'lucide-react';

const StockItem = ({ stock, onEdit, onRemove, onPause, engageAutomation }) => {
  const { setEditStock } = useStockStore();
  const { brokerName } = useActiveBroker();

  // Capital per unit for quantity calculation (same as AddWatchlistModal)
  const getCapitalPerUnit = (symbol, type) => {
    if (symbol === 'SPX') return type === 'single-leg' ? 4500 : 250;
    if (symbol === 'SPY' || symbol === 'QQQ') return type === 'single-leg' ? 450 : 200;
    return 0;
  };

  // Calculate quantity (number of contracts/spreads) from maxPerTrade
  const getQuantity = () => {
    const capitalPerUnit = getCapitalPerUnit(stock.symbol, stock.type);
    if (capitalPerUnit > 0 && stock.quanlity > 0) {
      return Math.floor(stock.quanlity / capitalPerUnit);
    }
    return 0;
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'active':
        return 'text-green-500';
      case 'filled':
        return 'text-blue-500';
      case 'pause':
      case 'paused':
      case 'exiting':
      case 'remove':
        return 'text-red-500';
      case 'pending':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatStatus = (status) => {
    if (!status || typeof status !== 'string') return '';
    const s = status.trim().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // Determine if the stock is engaged (has a non-empty status)
  const isEngaged = stock.status !== null && stock.status !== undefined && stock.status !== '';

  // ปุ่ม Edit และ Remove disabled ถ้า stock ยังไม่ได้ engage (ไม่มี status)
  const isEditRemoveDisabled = !isEngaged;

  // ปุ่ม Remove (Exit) ใช้งานได้เสมอเมื่อ stock มี status (ไม่ disable ในสถานะใดๆ)
  const isRemoveDisabled = false;

  // Log button click based on current status
  const logButtonClick = async (buttonName) => {
    const currentStatus = stock.status?.toLowerCase();
    let shouldLog = false;

    // Determine if button should be logged based on status
    if (currentStatus === 'engage' || currentStatus === 'active') {
      // ถ้าสถานะเป็น engage, Active -> ล็อกทุกปุ่ม
      shouldLog = true;
    } else if (currentStatus === 'pause' || currentStatus === 'paused') {
      // ถ้าสถานะเป็น pause, Paused -> ล็อกทุกปุ่ม
      shouldLog = true;
    } else if (currentStatus === 'pending') {
      // ถ้าสถานะเป็น pending -> ล็อกทุกปุ่ม
      shouldLog = true;
    } else if (currentStatus === 'filled') {
      // ถ้าสถานะเป็น filled -> ล็อกทุกปุ่ม
      shouldLog = true;
    } else if (currentStatus === 'exiting' || currentStatus === 'remove') {
      // ถ้าสถานะเป็น exiting, remove -> ล็อกทุกปุ่ม
      shouldLog = true;
    } else {
      // ถ้าไม่มีสถานะ -> ล็อกปุ่ม Engage เท่านั้น
      if (buttonName === 'Engage') {
        shouldLog = true;
      }
    }

    if (shouldLog) {
      try {
        const logMessage = `${buttonName} ${stock.symbol}`;
        
        // Upsert one engagement doc per user+symbol (no duplicates)
        const accountName = auth.currentUser?.displayName || auth.currentUser?.email || 'Unknown User';
        await setDoc(
          doc(db, 'tbl_engagement', `${accountName}_${stock.symbol}`),
          {
            username: accountName,
            userId: auth.currentUser?.uid || 'anonymous',
            broker: brokerName || 'Unknown',
            symbol: stock.symbol,
            status: buttonName.toLowerCase(),
            maxPerTrade: stock.quanlity || 0,
            timestamp: serverTimestamp()
          },
          { merge: true }
        );
        console.log(`Successfully logged ${buttonName} action to tbl_engagement:`, logMessage);

        // Also log to tbl_notifications
        await addDoc(collection(db, 'tbl_notifications'), {
          log: logMessage,
          timestamp: serverTimestamp(),
          userId: auth.currentUser?.uid || 'anonymous',
          symbol: stock.symbol,
          status: buttonName,
          action: buttonName,
          stockStatus: stock.status,
          stockType: stock.type,
          stockQuanlity: stock.quanlity,
          stockValue: stock.value,
          type: 'button_click'
        });
        console.log(`Successfully logged ${buttonName} action to tbl_notifications:`, logMessage);
      } catch (error) {
        console.error(`Error logging ${buttonName} action to Firebase:`, error);
      }
    }
  };

  return (
    <div className="bg-gray-800 p-2 rounded-md relative shadow border border-gray-700/40 flex items-stretch min-h-[56px]">
      {/* Info Section */}
      <div className="flex-1 flex flex-col justify-center pl-2">
        <div className="flex items-center gap-2">
          <span className="text-white text-lg font-extrabold leading-tight">{stock.symbol}</span>
          {/* Only display status if the stock is engaged */}
          {isEngaged && (
            <span className={`ml-2 text-xs font-medium ${getStatusColor(stock.status)} opacity-80`}>
              {formatStatus(stock.status)}
            </span>
          )}

        </div>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          <span className="text-gray-200 text-sm font-medium">
            ${stock.quanlity?.toLocaleString() || '0'}
          </span>
          {stock.type && (
            <>
              <span className="text-gray-300 text-sm">
                {getQuantity()} {stock.type === 'spread' ? 'Spread(s)' : 'Single Leg(s)'}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Button Section */}
      <div className="flex items-center gap-2 pr-2">
        {/* Edit */}
        <button
          onClick={() => {
            if (isEditRemoveDisabled) return; // Don't allow edit if not engaged
            // Don't log Edit button click - only log actions in modal
            onEdit();
          }}
          className={`px-3 py-1 text-white text-sm rounded transition-colors ${isEditRemoveDisabled ? 'opacity-50 cursor-not-allowed bg-gray-500' : 'bg-blue-500 hover:bg-blue-600'}`}
          style={{ minWidth: 60 }}
          disabled={isEditRemoveDisabled}
        >
          Edit
        </button>

        {/* Remove */}
        <button
          onClick={() => {
            if (isEditRemoveDisabled || isRemoveDisabled) return; // Don't allow remove if not engaged or in pending/paused state
            onRemove(); // This will open the confirmation modal
          }}
          className={`px-3 py-1 text-white text-sm rounded transition-colors ${(isEditRemoveDisabled || isRemoveDisabled) ? 'opacity-50 cursor-not-allowed bg-gray-500' : 'bg-red-500 hover:bg-red-600'}`}
          style={{ minWidth: 70 }}
          disabled={isEditRemoveDisabled || isRemoveDisabled}
        >
          Remove
        </button>
      </div>
    </div>
  );
};

export default StockItem;
